import {
  useRef,
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
} from "react";

type ScrollPositionState = {
  isScrolledToTop: boolean;
  isScrolledToBottom: boolean;
  isScrollable: boolean;
  hasMeasured: boolean;
};

export const useScrollPosition = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<ScrollPositionState>({
    isScrolledToTop: true,
    isScrolledToBottom: true,
    isScrollable: false,
    hasMeasured: false,
  });
  const tolerance = 10;

  const checkScrollPosition = useCallback(() => {
    const element = ref.current;
    if (!element) return;

    const { scrollTop, scrollHeight, clientHeight } = element;
    const nextData = {
      isScrollable: scrollHeight > clientHeight,
      isScrolledToTop: scrollTop < tolerance,
      isScrolledToBottom: scrollTop + clientHeight >= scrollHeight - tolerance,
      hasMeasured: true,
    };

    setData((prevData) =>
      prevData.isScrollable === nextData.isScrollable &&
      prevData.isScrolledToTop === nextData.isScrolledToTop &&
      prevData.isScrolledToBottom === nextData.isScrolledToBottom &&
      prevData.hasMeasured === nextData.hasMeasured
        ? prevData
        : nextData
    );
  }, []);

  useLayoutEffect(() => {
    checkScrollPosition();
  }, [checkScrollPosition]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    element.addEventListener("scroll", checkScrollPosition);

    let frameId = window.requestAnimationFrame(checkScrollPosition);
    let resizeObserver: ResizeObserver | undefined;

    if ("ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(() => {
        checkScrollPosition();
      });
      resizeObserver.observe(element);
    }

    return () => {
      element.removeEventListener("scroll", checkScrollPosition);
      window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
    };
  }, [checkScrollPosition]);

  return {
    ref,
    data,
    recalculate: checkScrollPosition,
  };
};
